/**
 * userIdentitySubscription — persistent relay subscription for the active
 * user's own replaceable events.
 *
 * Keeps a live subscription open on the UNION of the user's outbox relays AND
 * the configured lookup/index and fallback relays for replaceable events that
 * define who the user is, where they publish, and what they follow:
 *
 *   kind 0     — profile metadata
 *   kind 3     — contact / follow list
 *   kind 10002 — NIP-65 relay list (mailboxes)
 *   kind 10017 — NIP-51 Git authors follow list
 *   kind 10018 — NIP-51 Git repositories follow list
 *   kind 10063 — Blossom server list
 *   kind 10317 — Grasp server list
 *   kind 10318 — encrypted private Git relay list
 *   kind 10617 — pinned git repositories list
 *   kind 30078 — exact gitworkshop-preferences coordinate (separate filter)
 *
 * Events are piped directly into the EventStore (and therefore the IndexedDB
 * cache via persistEventsToCache) so they are immediately available to any
 * model or action that reads from the store.
 *
 * This is the foundation for robust replaceable-event actions: by keeping
 * these kinds continuously up-to-date from ALL of the user's outbox relays
 * AND index relays, we ensure that any action that modifies a replaceable
 * event always starts from the freshest known state — even if the user
 * updated the event on a different client that publishes to relays we
 * haven't queried yet.
 *
 * Usage (called from accounts.ts on active account change):
 *
 *   const stop = startUserIdentitySubscription(pubkey, outboxRelays);
 *   // later:
 *   stop();
 */

import { Subscription, combineLatest, of, from, timeout } from "rxjs";
import { map, distinctUntilChanged } from "rxjs/operators";
import { mapEventsToStore } from "applesauce-core";
import { onlyEvents } from "applesauce-relay";
import { eventStore, pool } from "./nostr";
import { lookupRelays, fallbackRelays } from "./settings";
import { cacheRequest } from "./cache";
import {
  PREFERENCES_KIND,
  PREFERENCES_IDENTIFIER,
} from "@/casts/GitWorkshopPreferences";
import type { Filter } from "applesauce-core/helpers";
import { resilientSubscription } from "@/lib/resilientSubscription";
import { createRelaySubscriptionCoverage } from "@/lib/relaySubscriptionCoverage";
import { normalizeUrl } from "@/lib/url";
import {
  USER_IDENTITY_COVERAGE_SETTLEMENT_TIMEOUT_MS,
  userIdentityCoverage,
} from "@/services/userIdentityCoverage";
import { PERSONAL_SINGLETON_KINDS } from "@/lib/personalSingletons";
import { startUserPersonalDeletionSubscription } from "@/services/userPersonalDeletionSubscription";

/**
 * All replaceable event kinds that define the user's identity, relay
 * configuration, and list preferences. Any kind added here will be
 * persistently subscribed to for the active user's session.
 *
 * The canonical policy list lives in personalSingletons.ts. Keep this export
 * for callers that treat the identity subscription as the source of its exact
 * filter.
 */
export const USER_REPLACEABLE_KINDS = PERSONAL_SINGLETON_KINDS;

/**
 * Open a persistent subscription for the user's replaceable events on the
 * UNION of their outbox, lookup/index, and fallback relays.
 *
 * Using both relay sets (not one as fallback for the other) ensures we
 * catch updates published from other clients that may have written to
 * index relays but not to all of the user's outbox relays, or vice versa.
 *
 * outboxRelays may be a static string[] (snapshot at call time) or an
 * Observable<string[]> that emits as the relay list grows. When an observable
 * is provided, new relays are added additively without tearing down existing
 * connections.
 *
 * Returns a cleanup function — call it when the account changes or logs out.
 */
export function startUserIdentitySubscription(
  pubkey: string,
  outboxRelays: string[] | import("rxjs").Observable<string[]>,
): () => void {
  const filter: Filter = {
    kinds: [...USER_REPLACEABLE_KINDS],
    authors: [pubkey],
  };

  const preferencesFilter = {
    kinds: [PREFERENCES_KIND],
    authors: [pubkey],
    "#d": [PREFERENCES_IDENTIFIER],
  } as Filter;

  // Keep deletion discovery on its existing outbox/lookup frontier.
  // Build a reactive relay list: union of outboxRelays + lookupRelays,
  // normalized and deduplicated. lookupRelays is a BehaviorSubject so changes
  // to the configured lookup relays are picked up automatically.
  const outbox$ =
    outboxRelays instanceof Array ? of(outboxRelays) : outboxRelays;

  const deletionRelays$ = combineLatest([outbox$, lookupRelays]).pipe(
    map(([outbox, lookup]) => [
      ...new Set([...outbox, ...lookup].map(normalizeUrl)),
    ]),
    distinctUntilChanged(
      (a, b) => a.length === b.length && a.every((v, i) => v === b[i]),
    ),
  );

  // One stable two-filter request serves identity and application preferences.
  // Each writer still selects its own voting groups from this shared coverage.
  const relays$ = combineLatest([deletionRelays$, fallbackRelays]).pipe(
    map(([identity, fallback]) =>
      [...new Set([...identity, ...fallback].map(normalizeUrl))].sort(),
    ),
    distinctUntilChanged(
      (a, b) => a.length === b.length && a.every((url, i) => url === b[i]),
    ),
  );

  // Personal-singleton warm coverage is owned by this exact account/filter
  // subscription. See docs/replaceable-preflight.md, "Warm coverage leases".
  const coverage = createRelaySubscriptionCoverage({
    settlementTimeoutMs: USER_IDENTITY_COVERAGE_SETTLEMENT_TIMEOUT_MS,
  });
  let stopped = false;
  let releaseCoverage: (() => void) | undefined;
  const stopCoverage = () => {
    if (stopped) return;
    stopped = true;
    releaseCoverage?.();
    coverage.stop();
  };

  // resilientSubscription provides:
  //   - lastReceivedAt-aware reconnect (avoids replaying full relay history)
  //   - foreground resume gap-fill (recovers events missed while backgrounded)
  //   - EOSE settle signal (not critical here but harmless)
  const sub: Subscription = resilientSubscription(
    pool,
    relays$,
    [filter, preferencesFilter],
    {
      reconnect: true,
      gapFill: true,
      settle: false, // no consumer needs the EOSE signal here
      paginate: false,
      // This lease gates all personal replaceable writes for the session. A
      // boot-time relay cap or transient outage must not make it terminal.
      retryCount: Infinity,
      onRelayLifecycle: (event) => coverage.onLifecycle(event),
    },
  )
    .pipe(onlyEvents(), mapEventsToStore(eventStore))
    .subscribe({
      error: (err) => {
        stopCoverage();
        console.warn("[userIdentitySubscription] subscription error:", err);
      },
    });

  // Subscribe before publishing the new owner. relays$ emits synchronously, so
  // the handle already contains its initial lifecycle facts when activate()
  // announces it. A pending writer therefore observes either no lease (the
  // deliberate hand-off gap) or a successor capable of settling, never an
  // empty successor that looks terminal.
  if (!stopped) {
    releaseCoverage = userIdentityCoverage.activate(pubkey, coverage);
  }
  const stopDeletionCoverage = startUserPersonalDeletionSubscription(
    pubkey,
    deletionRelays$,
  );

  // Restore the exact app coordinate without invoking an address loader.
  // Missing-state writers retain a bounded cache check if this read failed.
  const cacheSub = from(cacheRequest([preferencesFilter]))
    .pipe(timeout(1_000))
    .subscribe({
      next: (events) => {
        if (!stopped) events.forEach((event) => eventStore.add(event));
      },
      error: (error) =>
        console.warn(
          "[userIdentitySubscription] preference cache hydration failed:",
          error,
        ),
    });

  return () => {
    cacheSub.unsubscribe();
    stopDeletionCoverage();
    stopCoverage();
    sub.unsubscribe();
  };
}
